import {type EChartProps} from '@hm/fundamental-echarts'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, GRID_LINE_COLOR, MONTH_KEYS, SERIES_COLORS, TOOLTIP_BG, ZONE_LABEL_COLOR} from '../consts'
import {DifficultyFilter, MonthlyHierarchyAnalysisDataPoint, ValueDisplayMode} from '../types'
import {
  buildMarkLineConfig,
  buildPerNodeMonthlyDifficulty,
  buildSeriesData,
  computeBoundariesFromData,
  createTooltipFormatter
} from '../utils'

type HierarchyEntry = {id: string; name: string}

export type UseMonthlyChartOptionParams = {
  monthlyData: MonthlyHierarchyAnalysisDataPoint[]
  year: number
  hierarchyEntries: HierarchyEntry[]
  effectiveVisibleIds: string[]
  difficultyFilter: DifficultyFilter
  valueDisplayMode: ValueDisplayMode
  enabled?: boolean
}

const getDaysInMonth = (month: number, year: number): number =>
  new Date(year, month + 1, 0).getDate()

export const useMonthlyChartOption = ({
  monthlyData,
  year,
  hierarchyEntries,
  effectiveVisibleIds,
  difficultyFilter,
  valueDisplayMode,
  enabled = true
}: UseMonthlyChartOptionParams): EChartProps['option'] => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return useMemo(() => {
    if (!enabled) return {}

    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth() // 0-indexed

    const decLabel = t('comparativeAnalysis.chart.months.dec')
    const decFullLabel = t('comparativeAnalysis.chart.monthsFull.dec')

    // 13-item x-axis: index 0 = "31. Dec" (prev year), indices 1-12 = end-of-month labels for year
    // Hide current+future months when viewing current year
    const xAxisLabels: string[] = [`${getDaysInMonth(11, year - 1)}. ${decLabel}`]
    MONTH_KEYS.forEach((key, i) => {
      if (year === currentYear && i >= currentMonth) {
        xAxisLabels.push('')
      } else {
        const days = getDaysInMonth(i, year)
        const shortLabel = t(`comparativeAnalysis.chart.months.${key}`)
        xAxisLabels.push(`${days}. ${shortLabel}`)
      }
    })

    const monthFullLabels = MONTH_KEYS.map((key) =>
      t(`comparativeAnalysis.chart.monthsFull.${key}`)
    )

    const isPercentage = valueDisplayMode === 'percentage'
    const isSingleDifficulty = difficultyFilter !== 'all'
    const rawBoundaries = computeBoundariesFromData(monthlyData)
    const boundaries = isSingleDifficulty ? undefined : rawBoundaries

    const maxQuestionCount = monthlyData.reduce(
      (max, dp) => Math.max(max, dp.totalScore?.questionCount ?? 0),
      0
    )

    const basicLabel = t('comparativeAnalysis.card.basic')
    const advancedLabel = t('comparativeAnalysis.card.advanced')
    const excellenceLabel = t('comparativeAnalysis.card.excellence')

    // 13-item null placeholder for the grid series
    const gridSeries = {
      name: '',
      type: 'line',
      data: xAxisLabels.map(() => null),
      smooth: true,
      emphasis: {focus: 'none'},
      zlevel: 0,
      ...(isPercentage ? {} : buildMarkLineConfig(boundaries, maxQuestionCount))
    }

    const allDataSeries = buildSeriesData(hierarchyEntries, monthlyData, valueDisplayMode, year)
    const dataSeries = allDataSeries.filter((_, i) =>
      effectiveVisibleIds.includes(hierarchyEntries[i].id)
    )

    // Y-axis max: highest actual plotted value + 10% headroom
    const maxDataValue = dataSeries.reduce((max, s) => {
      const seriesMax = (s.data as (number | null)[]).reduce<number>(
        (m, v) => (v !== null && v > m ? v : m),
        0
      )
      return Math.max(max, seriesMax)
    }, 0)
    const yAxisMax = isPercentage ? 100 : maxDataValue > 0 ? Math.ceil(maxDataValue * 1.1) : undefined

    const series = [gridSeries, ...dataSeries]

    const visibleEntries = hierarchyEntries.filter((e) => effectiveVisibleIds.includes(e.id))
    const perNodeDifficulty = buildPerNodeMonthlyDifficulty(visibleEntries, monthlyData, year)

    const colorMap = new Map(
      hierarchyEntries.map((e, i) => [e.name, SERIES_COLORS[i % SERIES_COLORS.length]])
    )

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: TOOLTIP_BG,
        borderColor: TOOLTIP_BG,
        textStyle: {color: '#FFFFFF'},
        formatter: createTooltipFormatter({
          basicLabel,
          advancedLabel,
          excellenceLabel,
          perNodeDifficulty,
          difficultyFilter,
          valueDisplayMode,
          colorMap,
          getTitle: (dataIndex) =>
            dataIndex === 0
              ? t('comparativeAnalysis.chart.tooltipTitle', {month: decFullLabel, year: year - 1})
              : t('comparativeAnalysis.chart.tooltipTitle', {
                  month: monthFullLabels[dataIndex - 1],
                  year
                })
        })
      },
      legend: {show: false},
      grid: {
        left: !isPercentage && boundaries ? 30 : 16,
        right: 50,
        top: 16,
        bottom: 40,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisLabels,
        boundaryGap: false,
        axisLabel: {interval: 0, fontSize: 11},
        name: String(year),
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: {fontSize: 12, color: ZONE_LABEL_COLOR}
      },
      yAxis: [
        {
          type: 'value',
          name: isPercentage
            ? t('comparativeAnalysis.chart.yAxisLabelPercentage')
            : t('comparativeAnalysis.chart.yAxisLabel'),
          min: 0,
          ...(isPercentage
            ? {
                max: 100,
                interval: 10,
                splitLine: {show: true, lineStyle: {type: 'solid', color: GRID_LINE_COLOR}},
                axisLabel: {formatter: '{value}%'}
              }
            : {
                minInterval: 1,
                splitLine: {show: false},
                ...(yAxisMax !== undefined ? {max: yAxisMax} : {})
              })
        }
      ],
      series
    }
  }, [
    enabled,
    monthlyData,
    year,
    hierarchyEntries,
    t,
    difficultyFilter,
    effectiveVisibleIds,
    valueDisplayMode
  ])
}
